import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SearchCheck, ExternalLink, Bookmark, CheckCircle2, AlertTriangle, Lock, Info } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ProviderList } from '../components/ProviderList';

const TRI = [['approved', 'Yes, approved'], ['applied', 'Applied, waiting'], ['none', 'No / not sure']];

export default function MissingBenefits() {
  const [dtc, setDtc] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tracked, setTracked] = useState({});

  const run = async () => {
    setLoading(true);
    try {
      const body = dtc ? { dtc_status: dtc } : {};
      const { data } = await api.post('/audit/odsp', body);
      setResult(data);
      const apps = await api.get('/applications');
      const m = {}; apps.data.applications.forEach((a) => { m[a.program_id] = a; }); setTracked(m);
    } catch { toast.error('Could not run the audit. Please try again.'); }
    finally { setLoading(false); }
  };

  const trackIt = async (g) => {
    const { data } = await api.post('/applications', { program_id: g.id, status: 'researching', next_action: g.next_step });
    setTracked((t) => ({ ...t, [g.id]: data }));
    toast.success(`Added "${g.name}" to My Applications`);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C26227]/10 text-[#C26227] text-xs font-medium mb-4">
        <SearchCheck className="w-3.5 h-3.5" /> For current ODSP recipients
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>I already receive ODSP — what am I missing?</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">ODSP does not automatically enrol you in federal programs. Most recipients are missing the Disability Tax Credit, which unlocks the Canada Disability Benefit and RDSP. Run a one-tap audit to see your gaps.</p>

      <Card className="p-5 sm:p-6 mt-8 bg-card">
        <div className="text-sm font-medium mb-2">Optional: Do you have Disability Tax Credit (DTC) approval from CRA?</div>
        <div className="flex gap-2 flex-wrap">
          {TRI.map(([v, l]) => (
            <Button key={v} size="sm" variant={dtc === v ? 'default' : 'outline'} className={dtc === v ? 'bg-primary text-primary-foreground' : ''} onClick={() => setDtc(v)} data-testid={`audit-dtc-${v}`}>{l}</Button>
          ))}
        </div>
        <Button onClick={run} disabled={loading} size="lg" className="mt-5 h-12 bg-[#C26227] hover:bg-[#C26227]/90 text-white" data-testid="run-audit-btn">
          <SearchCheck className="w-4 h-4 mr-2" /> {loading ? 'Checking…' : 'Check what I may be missing'}
        </Button>
      </Card>

      {result && (
        <div className="mt-8" data-testid="audit-results">
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <h2 className="text-lg font-semibold" style={{fontFamily: 'Lexend'}}>Possible gaps</h2>
            <Badge className="bg-[#FFF4E5] text-[#663C00] border-[#FFE0B2] border" data-testid="audit-gap-count">{result.gaps.length} programs to look into</Badge>
            <Badge className="bg-[#E6F4EA] text-[#0F5132] border-[#A3CFBB] border" data-testid="audit-covered-count">{result.covered.length} already covered</Badge>
          </div>
          <div className="grid gap-4">
            {result.gaps.map((g) => {
              const locked = g.requires_dtc && result.dtc_status !== 'approved';
              const isTracked = !!tracked[g.id];
              return (
                <Card key={g.id} className="p-5 bg-card" data-testid={`gap-card-${g.id}`}>
                  <div className="flex flex-col md:flex-row gap-4 justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {locked ? <Badge className="bg-[#EBF3FA] text-[#13426B] border-[#B8D5E5] border"><Lock className="w-3 h-3 mr-1" /> Needs DTC first</Badge> : <Badge className="bg-[#FFF4E5] text-[#663C00] border-[#FFE0B2] border"><AlertTriangle className="w-3 h-3 mr-1" /> Likely gap</Badge>}
                        <Badge variant="outline">{g.government}</Badge>
                        {g.tracked_status && <Badge variant="outline">Tracking: {g.tracked_status.replace('_', ' ')}</Badge>}
                      </div>
                      <h3 className="text-xl font-bold" style={{fontFamily: 'Lexend'}}>{g.name}</h3>
                      <p className="text-sm text-foreground/80 mt-1.5">{g.why}</p>
                      <div className="mt-3 p-3 rounded-lg bg-secondary text-sm flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" /><span><strong>Next step:</strong> {g.next_step}</span></div>
                      <div className="text-sm mt-3"><span className="text-muted-foreground">Est. value:</span> <span className="font-semibold text-primary">{g.estimated_amount}</span></div>
                    </div>
                    <div className="flex gap-2 shrink-0 md:flex-col">
                      {isTracked ? <Button variant="outline" disabled data-testid={`gap-tracked-${g.id}`}><CheckCircle2 className="w-4 h-4 mr-2 text-primary" /> Tracking</Button>
                        : <Button variant="outline" onClick={() => trackIt(g)} data-testid={`gap-track-${g.id}`}><Bookmark className="w-4 h-4 mr-2" /> Track</Button>}
                      <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground"><a href={g.application_url} target="_blank" rel="noopener noreferrer">Official site <ExternalLink className="w-4 h-4 ml-2" /></a></Button>
                    </div>
                  </div>
                  {(g.id === 'adp_wheelchair' || g.id === 'cohb') && (
                    <Accordion type="single" collapsible className="mt-3">
                      <AccordionItem value="prov" className="border-t border-border pt-1">
                        <AccordionTrigger className="text-sm font-medium" data-testid={`gap-providers-${g.id}`}>Who can help locally?</AccordionTrigger>
                        <AccordionContent><ProviderList programId={g.id} /></AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </Card>
              );
            })}
          </div>
          {result.covered.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold mb-3" style={{fontFamily: 'Lexend'}}>Already covered</h2>
              <ul className="space-y-1.5">
                {result.covered.map((c) => <li key={c.id} className="text-sm flex items-center gap-2" data-testid={`covered-${c.id}`}><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {c.name}</li>)}
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-6">This is a screening based on typical ODSP recipient patterns and your saved profile. The government makes the final eligibility decision.</p>
        </div>
      )}
    </div>
  );
}
